import React, { useEffect, useMemo, useState } from 'react';
import { contactService } from '../../../services/contactService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const PeoplePicker = ({ selectedIds, initialPeople = [], onChange }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    contactService.getContacts()
      .then(setContacts)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  const known = useMemo(() => {
    const map = new Map();
    initialPeople.forEach((p) => map.set(p.id, p));
    contacts.forEach((c) => map.set(c.id, c));
    return map;
  }, [contacts, initialPeople]);

  const q = query.trim().toLowerCase();
  const matches = q
    ? contacts
        .filter((c) => !selectedIds.includes(c.id))
        .filter((c) => `${c.full_name} ${c.company || ''} ${c.email || ''}`.toLowerCase().includes(q))
        .slice(0, 5)
    : [];

  const add = (id) => { onChange([...selectedIds, id]); setQuery(''); };
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5 tracking-tight">Who was there? (optional)</label>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedIds.map((id) => (
            <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 pl-3 pr-1.5 py-1 text-sm font-medium text-slate-700">
              {known.get(id)?.full_name || 'Contact'}
              <button type="button" onClick={() => remove(id)} className="p-0.5 rounded-full hover:bg-slate-200" aria-label="Remove person">
                <SafeIcon icon={FiIcons.FiX} />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={loading ? 'Loading your contacts...' : 'Search your contacts by name, company or email'}
        disabled={loading || failed}
        className="block w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:border-meetoi-navy focus:ring-meetoi-navy/10 sm:text-sm shadow-sm"
      />
      {failed && <p className="mt-1.5 text-sm text-red-600">Could not load your contacts. You can still save without people.</p>}
      {q && matches.length === 0 && !failed && (
        <p className="mt-1.5 text-sm text-slate-500">No matching contacts. Add people on the Contacts page first.</p>
      )}
      {matches.length > 0 && (
        <ul className="mt-2 rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {matches.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => add(c.id)} className="w-full text-left px-3.5 py-2.5 hover:bg-slate-50">
                <span className="block text-sm font-semibold text-slate-900">{c.full_name || 'Unnamed contact'}</span>
                {(c.company || c.email) && <span className="block text-xs text-slate-500">{c.company || c.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};